import { useCallback, useEffect, useState } from "react"
import QRCode from "qrcode"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useClinicalQrTools({
  clinicId,
  clinicName,
  isReadOnly,
  readOnlyReason,
}) {
  const [qrLoading, setQrLoading] = useState(false)
  const [qrGenerating, setQrGenerating] = useState(false)
  const [qrInfo, setQrInfo] = useState(null)
  const [qrImageUrl, setQrImageUrl] = useState("")
  const [qrImageLoading, setQrImageLoading] = useState(false)

  const loadQrInfo = useCallback(async () => {
    try {
      if (!clinicId) {
        setQrInfo(null)
        return
      }

      setQrLoading(true)

      const res = await apiFetch(
        `${API_BASE}/clinical-history/qr-link?clinicId=${clinicId}`
      )
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setQrInfo(null)
        return
      }

      setQrInfo(data)
    } catch {
      setQrInfo(null)
    } finally {
      setQrLoading(false)
    }
  }, [clinicId])

  const buildQrImage = useCallback(async () => {
    try {
      const url = qrInfo?.link?.publicUrl || ""

      if (!url) {
        setQrImageUrl("")
        return
      }

      setQrImageLoading(true)

      const dataUrl = await QRCode.toDataURL(url, {
        width: 168,
        margin: 1,
        errorCorrectionLevel: "M",
        color: {
          dark: "#0f172a",
          light: "#ffffff",
        },
      })

      setQrImageUrl(dataUrl)
    } catch {
      setQrImageUrl("")
    } finally {
      setQrImageLoading(false)
    }
  }, [qrInfo])

  const handleGenerateQrLink = useCallback(async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason)
        return
      }

      if (!clinicId) {
        alert("No hay clÃ­nica activa.")
        return
      }

      setQrGenerating(true)

      const res = await apiFetch(`${API_BASE}/clinical-history/qr-link/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ clinicId }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo generar el enlace QR.")
        return
      }

      setQrInfo(data)
      alert("Enlace QR generado correctamente.")
    } catch {
      alert("Error al generar el enlace QR.")
    } finally {
      setQrGenerating(false)
    }
  }, [clinicId, isReadOnly, readOnlyReason])

  const handleCopyQrLink = useCallback(async () => {
    try {
      const url = qrInfo?.link?.publicUrl
      if (!url) {
        alert("No hay enlace QR generado.")
        return
      }

      await navigator.clipboard.writeText(url)
      alert("Enlace copiado al portapapeles.")
    } catch {
      alert("No se pudo copiar el enlace.")
    }
  }, [qrInfo])

  const handleOpenQrLink = useCallback(() => {
    const url = qrInfo?.link?.publicUrl
    if (!url) {
      alert("No hay enlace QR disponible.")
      return
    }

    window.open(url, "_blank", "noopener,noreferrer")
  }, [qrInfo])

  const handleDownloadQr = useCallback(() => {
    try {
      if (!qrImageUrl) {
        alert("Primero genera o carga un QR vÃ¡lido.")
        return
      }

      const a = document.createElement("a")
      a.href = qrImageUrl
      a.download = `odontoflow-qr-clinico-${clinicId || "clinica"}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch {
      alert("No se pudo descargar el QR.")
    }
  }, [qrImageUrl, clinicId])

  const handlePrintQrPoster = useCallback(() => {
    try {
      if (!qrImageUrl) {
        alert("Primero genera o carga un QR vÃ¡lido.")
        return
      }

      const publicUrl = qrInfo?.link?.publicUrl || ""
      const safeClinicName = qrInfo?.clinicName || clinicName || "ClÃ­nica"

      const printWindow = window.open("", "_blank", "width=900,height=700")
      if (!printWindow) {
        alert("No se pudo abrir la ventana de impresiÃ³n.")
        return
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Pre-registro clÃ­nico</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 22px;
                color: #0f172a;
                background: #ffffff;
              }
              .sheet {
                max-width: 640px;
                margin: 0 auto;
                border: 1px solid #e2e8f0;
                border-radius: 20px;
                padding: 22px;
                text-align: center;
              }
              .badge {
                display: inline-block;
                padding: 6px 10px;
                border-radius: 999px;
                background: #eff6ff;
                color: #1d4ed8;
                font-size: 12px;
                font-weight: 700;
                margin-bottom: 12px;
              }
              h1 {
                margin: 0 0 8px 0;
                font-size: 26px;
                line-height: 1.1;
              }
              p {
                margin: 0 0 16px 0;
                font-size: 14px;
                line-height: 1.55;
                color: #475569;
              }
              img {
                width: 180px;
                height: 180px;
                object-fit: contain;
                margin: 12px auto;
                display: block;
              }
              .steps {
                text-align: left;
                margin: 16px auto 0;
                max-width: 380px;
                font-size: 13px;
                line-height: 1.7;
                color: #334155;
              }
              .url {
                margin-top: 16px;
                padding: 10px 12px;
                border-radius: 12px;
                background: #f8fafc;
                border: 1px solid #e2e8f0;
                font-size: 12px;
                color: #64748b;
                word-break: break-all;
              }
            </style>
          </head>
          <body>
            <div class="sheet">
              <div class="badge">Pre-registro clÃ­nico</div>
              <h1>${safeClinicName}</h1>
              <p>
                Escanea este cÃ³digo para completar tu pre-registro clÃ­nico antes de tu cita.
              </p>

              <img src="${qrImageUrl}" alt="QR clÃ­nico" />

              <div class="steps">
                <div>1. Abre la cÃ¡mara de tu telÃ©fono.</div>
                <div>2. Escanea el cÃ³digo QR.</div>
                <div>3. Completa el formulario clÃ­nico.</div>
                <div>4. EnvÃ­alo antes de pasar a consulta.</div>
              </div>

              <div class="url">${publicUrl}</div>
            </div>

            <script>
              window.onload = function() {
                window.print();
              }
            </script>
          </body>
        </html>
      `)

      printWindow.document.close()
    } catch {
      alert("No se pudo imprimir el cartel.")
    }
  }, [qrImageUrl, qrInfo, clinicName])

  useEffect(() => {
    if (!clinicId) return
    loadQrInfo()
  }, [clinicId, loadQrInfo])

  useEffect(() => {
    buildQrImage()
  }, [buildQrImage])

  return {
    qrLoading,
    qrGenerating,
    qrInfo,
    qrImageUrl,
    qrImageLoading,
    reloadQrInfo: loadQrInfo,
    handleGenerateQrLink,
    handleCopyQrLink,
    handleOpenQrLink,
    handleDownloadQr,
    handlePrintQrPoster,
  }
}
